/**
 * Fix a card where it sits on the trade, instead of removing and rescanning it. A customer's card
 * picks any printing and condition, with the market value for that choice. A store's card picks from
 * the copies you have in stock, since that is what it would sell. "Wrong card?" finds the right one
 * and this line becomes it. The server reprices the line either way.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { useCurrency } from '../../../contexts/CurrencyContext'
import { UnifiedCardSearch, type CardSearchResult } from '../../../components/cards/UnifiedCardSearch'
import { STANDARD_CONDITIONS } from '../../../utils/conditions'
import { estimatePlayedPriceCents } from '../../../utils/price'
import { choosePrinting } from '../../Scanner/printings'
import { lookupPrintings, lookupQuery, quoteFor } from '../../Scanner/scannerLookup'
import styles from '../CreateTransaction.module.css'

export type LineVariant = { printing: string; condition: string; cardId?: string }

export function TradeLineVariantEditor({ cardId, name, side, printing, condition, note, onSave, onCancel }: {
  cardId: string
  name: string
  side: 'store' | 'customer'
  printing: string
  condition: string
  /** Why it's being checked: the "Check printing" text. */
  note?: string | null
  /** Resolves false when it failed (the caller shows why, and the editor stays open). */
  onSave: (variant: LineVariant) => Promise<boolean>
  onCancel: () => void
}) {
  const { user } = useAuth()
  const { formatPrice } = useCurrency()
  /** The right card, once "Wrong card?" has found it. */
  const [replacement, setReplacement] = useState<{ id: string; name: string } | null>(null)
  const [searching, setSearching] = useState(false)
  const activeCardId = replacement?.id ?? cardId
  const lookup = useQuery(lookupQuery(user?.organizationId ?? '', activeCardId))
  const options = lookupPrintings(lookup.data?.card)
  const [chosenPrinting, setChosenPrinting] = useState(printing)
  const [chosenCondition, setChosenCondition] = useState(condition)
  const [saving, setSaving] = useState(false)
  const value = choosePrinting(options, chosenPrinting)
  const copies = (lookup.data?.card.store ?? []).filter((c) => !c.priceUnavailable)
  const copyKey = (c: LineVariant) => `${c.printing}|${c.condition}`
  const quote = quoteFor(lookup.data, value, chosenCondition)
  const market = quote.marketUsdCents
    ?? estimatePlayedPriceCents(quoteFor(lookup.data, value, 'Near Mint').marketUsdCents, chosenCondition)

  const save = async (variant: LineVariant) => {
    setSaving(true)
    const ok = await onSave(replacement ? { ...variant, cardId: replacement.id } : variant)
    setSaving(false)
    if (ok) onCancel()
  }

  return (
    <div className={styles.lineVariantEditor}>
      {note && <p className={styles.lineVariantNote}>{note}</p>}
      {replacement && <p className={styles.lineVariantNote}>Changing {name} to <b>{replacement.name}</b></p>}
      {searching ? (
        <UnifiedCardSearch
          searchType={side === 'store' ? 'inventory' : 'catalog'}
          placeholder={side === 'store' ? 'Search your inventory' : 'Search all cards'}
          showSetNumberInput={false}
          showGameFilter
          showSetFilter
          defaultSearchBy="cards"
          catalogVariantPreview="count"
          autoFocus
          onSelectCard={(picked) => {
            if ('cardName' in picked) {
              setReplacement({ id: picked.cardId, name: picked.cardName })
              setChosenPrinting(picked.printing)
              setChosenCondition(picked.condition)
            } else {
              const card = picked as CardSearchResult
              setReplacement({ id: card.id, name: card.name })
              setChosenPrinting('')
            }
            setSearching(false)
          }}
        />
      ) : (
        <button type="button" className={styles.lineVariantReplace} onClick={() => setSearching(true)}>Wrong card? Find the right one</button>
      )}
      {side === 'customer' ? (
        <>
          <div className={styles.lineVariantFields}>
            <select aria-label="Printing" value={value} disabled={saving} onChange={(e) => setChosenPrinting(e.target.value)}>
              {options.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select aria-label="Condition" value={chosenCondition} disabled={saving} onChange={(e) => setChosenCondition(e.target.value)}>
              {STANDARD_CONDITIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <p className={styles.lineVariantNote}>{lookup.isLoading ? 'Looking up the price…' : market ? `Market ${formatPrice(market, 'USD')}` : 'No market price: set one by hand after saving.'}</p>
        </>
      ) : lookup.isLoading ? (
        <p className={styles.lineVariantNote}>Looking up your stock…</p>
      ) : copies.length ? (
        <select aria-label="Which copy" value={copyKey({ printing: chosenPrinting, condition: chosenCondition })} disabled={saving}
          onChange={(e) => {
            const copy = copies.find((c) => copyKey(c) === e.target.value)
            if (copy) { setChosenPrinting(copy.printing); setChosenCondition(copy.condition) }
          }}>
          {!copies.some((c) => copyKey(c) === copyKey({ printing: chosenPrinting, condition: chosenCondition })) && (
            <option value={copyKey({ printing: chosenPrinting, condition: chosenCondition })}>{chosenPrinting} · {chosenCondition} (no stock)</option>
          )}
          {copies.map((c) => (
            <option key={c.productId} value={copyKey(c)}>{c.printing} · {c.condition} · {c.availableQuantity} in stock · {formatPrice(c.listedUsdCents ?? 0, 'USD')}</option>
          ))}
        </select>
      ) : (
        <p className={styles.lineVariantNote}>You have no other copies of this card in stock.</p>
      )}
      <div className={styles.lineVariantActions}>
        <button type="button" className={styles.lineVariantSave} disabled={saving || lookup.isLoading}
          onClick={() => void save({ printing: side === 'customer' ? value : chosenPrinting, condition: chosenCondition })}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={styles.lineVariantCancel} disabled={saving} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}
